import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { parseKMZ } from "./utils/kmzParser";
import MapSection from "./components/MapSection";
import SidebarPanel from "./components/SidebarPanel";
import DataEntryPopup from "./components/DataEntryPopup";
import LoginPage from "./components/LoginPage";
import AdminPanel from "./components/AdminPanel";
import TeamPersonnelModal from "./components/TeamPersonnelModal";
import SyncConflictPanel from "./components/SyncConflictPanel";
import ToolPageHost from "./components/ToolPageHost";
import CoordinateSearchModal from "./components/CoordinateSearchModal";
import {
  fetchApiHealth,
  fetchCurrentSession,
  fetchProjectPersonnel,
  importPafta,
  logout as apiLogout,
  updatePoint as apiUpdatePoint,
} from "./api";
import {
  getApiErrorMessage,
  clearStoredAuthState,
  getConflictCurrent,
  getStoredToken,
  getTokenStorageMode,
  isConflictError,
  setStoredToken,
} from "./api/client";
import {
  PROJECT_STORAGE_KEY,
  USER_STORAGE_KEY,
  getLegacySyncConflictKey,
  getLegacySyncQueueKey,
  getSyncConflictKey,
  getSyncQueueKey,
} from "./constants/storageKeys";
import { useGPS } from "./hooks/useGPS";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import {
  useSyncQueue,
  loadConflictsByKey,
  loadQueueByKey,
  saveConflictsByKey,
} from "./hooks/useSyncQueue";
import { useProjectData } from "./hooks/useProjectData";
import { useMeasurements } from "./hooks/useMeasurements";
import { applyPointServerFields } from "./utils/pointState";
import { createFileImportId } from "./utils/clientIds";
import { requestPersistentOfflineStorage } from "./offline/offlineStore";
import { reverseGeocodeLocation } from "./services/hgmAtlasService";
import {
  LEGACY_DEFAULT_PROJECT_ID,
  PACKAGING_PAGE,
  TOOL_PAGES,
  clearSessionValue,
  getGlobalRoleLabel,
  isAuthError,
  parseCoordinateSearch,
  readSessionValue,
  resolveAutoTeamIdForUser,
  writeSessionValue,
} from "./app/appConfig";

export default function App() {
  const [isPersistentSession, setIsPersistentSession] = useState(
    () => getTokenStorageMode() !== "session"
  );
  const [user, setUser] = useState(() => {
    try {
      const saved = readSessionValue(USER_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error("Kullanıcı bilgisi okunamadı:", error);
      return null;
    }
  });
  const [authReady, setAuthReady] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(
    () => {
      const saved = readSessionValue(PROJECT_STORAGE_KEY);
      return saved ? String(saved) : LEGACY_DEFAULT_PROJECT_ID;
    }
  );

  const isOnline = useOnlineStatus();
  const gpsPosition = useGPS();
  const [syncPulse, setSyncPulse] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncConflicts, setSyncConflicts] = useState([]);
  const [isConflictPanelOpen, setIsConflictPanelOpen] = useState(false);

  const [menuVisible, setMenuVisible] = useState(true);
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const [activeToolPage, setActiveToolPage] = useState(null);
  const [clickedInfo, setClickedInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [paftaTab, setPaftaTab] = useState("aktif");
  const [nattFilterStart, setNattFilterStart] = useState("");
  const [nattFilterEnd, setNattFilterEnd] = useState("");
  const [moveModeId, setMoveModeId] = useState(null);
  const [tempPosition, setTempPosition] = useState(null);
  const [clickMarker, setClickMarker] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedPaftaForTeams, setSelectedPaftaForTeams] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isAdminViewOpen, setIsAdminViewOpen] = useState(false);
  const [isOpeningAdminView, setIsOpeningAdminView] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [personnel, setPersonnel] = useState([]);
  const [projectAccessNotice, setProjectAccessNotice] = useState("");
  const [isCoordinateSearchOpen, setIsCoordinateSearchOpen] = useState(false);
  const [coordinateSearchX, setCoordinateSearchX] = useState("");
  const [coordinateSearchY, setCoordinateSearchY] = useState("");
  const [coordinateSearchError, setCoordinateSearchError] = useState("");
  const [coordinateSearchTarget, setCoordinateSearchTarget] = useState(null);
  const toolsMenuRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    requestPersistentOfflineStorage().catch(() => {
      // Tarayıcı kalıcı alan vermezse IndexedDB çalışmaya devam eder.
    });
  }, [user?.id]);

  useEffect(() => {
    // Koordinat araması geçicidir; sayfa/proje yeniden kurulduğunda haritada kalmamalıdır.
    setCoordinateSearchX("");
    setCoordinateSearchY("");
    setCoordinateSearchError("");
    setCoordinateSearchTarget(null);
  }, [selectedProjectId]);

  useEffect(() => {
    if (!clickMarker && !clickedInfo) return;
    setCoordinateSearchTarget(null);
  }, [clickMarker, clickedInfo]);

  useEffect(() => {
    const tokenStorageMode = getTokenStorageMode();
    const rememberSession = tokenStorageMode === "local";
    setIsPersistentSession(rememberSession);

    const token = getStoredToken();

    if (!isOnline) {
      setAuthReady(true);
      return;
    }

    let cancelled = false;
    const clearStoredSession = () => {
      clearStoredAuthState();
      setUser(null);
      clearSessionValue(USER_STORAGE_KEY);
      clearSessionValue(PROJECT_STORAGE_KEY);
    };

    const restoreSession = async () => {
      try {
        const session = await fetchCurrentSession({ remember: rememberSession });
        if (cancelled) return;

        if (!session?.user) {
          clearStoredSession();
          setAuthReady(true);
          return;
        }

        if (!token) {
          const remember = true;
          setStoredToken("1", { remember });
          setIsPersistentSession(remember);
        }

        setUser((previous) => {
          const selectedRole = session.user.projectRoles?.[selectedProjectId];
          const nextUser =
            session.user.projectRoles?.ALL === "superadmin"
              ? { ...session.user, role: "superadmin", projectId: null }
              : selectedRole
                ? { ...session.user, role: selectedRole, projectId: selectedProjectId }
                : previous || session.user;

          writeSessionValue(
            USER_STORAGE_KEY,
            JSON.stringify(nextUser),
            rememberSession
          );
          return nextUser;
        });
      } catch (error) {
        console.error("Oturum geri yüklenemedi:", error);
        if (isAuthError(error)) {
          clearStoredSession();
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [isOnline]);

  const handleLogin = (userData, options = {}) => {
    const remember = options?.remember !== false;
    setIsPersistentSession(remember);
    setUser(userData);
    writeSessionValue(USER_STORAGE_KEY, JSON.stringify(userData), remember);
    if (userData.projectId) {
      writeSessionValue(PROJECT_STORAGE_KEY, String(userData.projectId), remember);
      setSelectedProjectId(String(userData.projectId));
    }
  };

  const handleLogout = () => {
    const runLogout = async () => {
      await apiLogout();
      setUser(null);
      clearSessionValue(USER_STORAGE_KEY);
      clearSessionValue(PROJECT_STORAGE_KEY);
    };

    const confirmed = window.confirm("Oturumu kapatmak istediğinize emin misiniz?");
    if (!confirmed) return;

    runLogout().catch((error) => {
      console.error("Oturum kapatılamadı:", error);
      clearStoredAuthState();
      setUser(null);
      clearSessionValue(USER_STORAGE_KEY);
      clearSessionValue(PROJECT_STORAGE_KEY);
    });
  };

  const handleOpenAdminView = useCallback(async () => {
    if (isOpeningAdminView) return;

    if (!isOnline) {
      alert("Çevrimdışı modda admin paneli açılamaz.");
      return;
    }

    setIsOpeningAdminView(true);
    try {
      // Admin paneli canlı API verisi ister; backend yoksa boş ekrana düşürmeyelim.
      const health = await fetchApiHealth();
      if (health?.ok !== true) {
        throw new Error("API sağlık kontrolü başarısız.");
      }

      setIsToolsMenuOpen(false);
      setActiveToolPage(null);
      setIsAdminViewOpen(true);
    } catch (_error) {
      alert(
        "API sunucusuna bağlanılamadı. Admin paneli için backend'in 4000 portunda açık olması gerekir."
      );
    } finally {
      setIsOpeningAdminView(false);
    }
  }, [isOnline, isOpeningAdminView]);

  useEffect(() => {
    if (!isToolsMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!toolsMenuRef.current?.contains(event.target)) {
        setIsToolsMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [isToolsMenuOpen]);

  const handleToggleSidebarMenu = useCallback(() => {
    setMenuVisible((previous) => {
      const next = !previous;
      if (!next) {
        setIsToolsMenuOpen(false);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (user?.projectRoles?.[selectedProjectId]) {
      const nextRole = user.projectRoles[selectedProjectId];
      if (user.role !== nextRole || user.projectId !== selectedProjectId) {
        const updatedUser = { ...user, role: nextRole, projectId: selectedProjectId };
        setUser(updatedUser);
        writeSessionValue(
          USER_STORAGE_KEY,
          JSON.stringify(updatedUser),
          isPersistentSession
        );
      }
    }
  }, [selectedProjectId, user, isPersistentSession]);

  useEffect(() => {
    if (!selectedProjectId) return;
    writeSessionValue(
      PROJECT_STORAGE_KEY,
      String(selectedProjectId),
      isPersistentSession
    );
    setClickedInfo(null);
    setSearchTerm("");
    setPaftaTab("aktif");
    setSelectedTeamId(null);
    setMoveModeId(null);
    setTempPosition(null);
    setClickMarker(null);
    setSelectedPaftaForTeams(null);
    setIsTeamModalOpen(false);
  }, [selectedProjectId, isPersistentSession]);

  const { addToSyncQueue } = useSyncQueue({
    isOnline,
    canSync: authReady && Boolean(user),
    selectedProjectId,
    userId: user?.id,
    setSyncPulse,
  });

  const {
    projects,
    paftalar,
    points,
    teams,
    measurementTemplate,
    setPaftalar,
    setTeams,
    createEmptyProject,
    renameProject,
    deleteProject,
    changePointStatus,
    paftaGorunurlukDegistir,
    makePaftaPasif,
    makePaftaAktif,
    deletePafta,
    addNewTeam,
    deleteTeam,
    assignTeamToPoint,
    createPointLocalFirst,
    moveTemplateRow,
    addTemplateRow,
    renameTemplateRow,
    deleteTemplateRow,
    resetTemplateToDefault,
    handleAddMember,
    handleDeleteMember,
    setPoints,
    reloadWorkspaceFromServer,
  } = useProjectData({
    isOnline,
    selectedProjectId,
    setSelectedProjectId,
    addToSyncQueue,
    loadQueueByKey,
    syncPulse,
    ownerUserId: user?.id,
  });

  useEffect(() => {
    if (!selectedProjectId) return;
    if (selectedTeamId) return;
    if (!user || user.role !== "user") return;

    const autoTeamId = resolveAutoTeamIdForUser(teams, user);
    if (!autoTeamId) return;

    setSelectedTeamId(autoTeamId);
  }, [selectedProjectId, selectedTeamId, teams, user]);

  const handleProjectChange = useCallback(
    async (nextProjectId) => {
      const normalizedProjectId = String(nextProjectId || "").trim();
      if (!normalizedProjectId || normalizedProjectId === String(selectedProjectId || "")) {
        return;
      }

      setIsSwitching(true);
      setProjectAccessNotice("");

      try {
        setSelectedProjectId(normalizedProjectId);
        const reloaded = await reloadWorkspaceFromServer(normalizedProjectId);

        if (!reloaded && isOnline) {
          setProjectAccessNotice("Proje verileri yüklenemedi. Lütfen tekrar deneyin.");
        }
      } catch (error) {
        console.error("Proje değiştirilemedi:", error);
        setProjectAccessNotice(
          getApiErrorMessage(error, "Proje değiştirilemedi. Lütfen tekrar deneyin.")
        );
      } finally {
        setIsSwitching(false);
      }
    },
    [isOnline, reloadWorkspaceFromServer, selectedProjectId]
  );

  const authorizedProjectIds = useMemo(() => {
    if (!user || !Array.isArray(projects) || projects.length === 0) {
      return [];
    }

    if (user.role === "superadmin" || user.projectRoles?.ALL === "superadmin") {
      return projects.map((project) => project.id);
    }

    if (user.projectRoles && typeof user.projectRoles === "object") {
      const allowedIds = new Set(
        Object.keys(user.projectRoles).filter((roleKey) => roleKey !== "ALL")
      );
      return projects
        .map((project) => project.id)
        .filter((projectId) => allowedIds.has(String(projectId)));
    }

    if (user.projectId) {
      return projects
        .filter((project) => String(project.id) === String(user.projectId))
        .map((project) => project.id);
    }

    return [];
  }, [projects, user]);

  useEffect(() => {
    if (!user || !projects.length || authorizedProjectIds.length === 0) {
      return;
    }

    if (!selectedProjectId) {
      setSelectedProjectId(String(authorizedProjectIds[0]));
      return;
    }

    const hasAccess = authorizedProjectIds.some(
      (projectId) => String(projectId) === String(selectedProjectId)
    );
    if (hasAccess) return;

    const fallbackProjectId = authorizedProjectIds[0];
    const fallbackProjectName =
      projects.find((project) => String(project.id) === String(fallbackProjectId))?.name ||
      "yetkili proje";
    const blockedProjectName =
      projects.find((project) => String(project.id) === String(selectedProjectId))?.name || "";

    setSelectedProjectId(String(fallbackProjectId));
    setProjectAccessNotice(
      blockedProjectName
        ? `"${blockedProjectName}" projesine erişiminiz olmadığı için "${fallbackProjectName}" projesine geçildi.`
        : `Seçili proje yetkiniz dışında kaldığı için "${fallbackProjectName}" projesine geçildi.`
    );
  }, [authorizedProjectIds, projects, selectedProjectId, user]);

  useEffect(() => {
    if (!projectAccessNotice) return undefined;

    const timer = setTimeout(() => {
      setProjectAccessNotice("");
    }, 6000);

    return () => clearTimeout(timer);
  }, [projectAccessNotice]);

  const refreshSyncState = useCallback(async () => {
    if (!selectedProjectId) {
      setPendingCount(0);
      setSyncConflicts([]);
      return;
    }

    const queueKey = getSyncQueueKey(user?.id, selectedProjectId);
    const conflictKey = getSyncConflictKey(user?.id, selectedProjectId);
    const [queue, conflicts] = await Promise.all([
      loadQueueByKey(queueKey, getLegacySyncQueueKey(selectedProjectId)),
      loadConflictsByKey(
        conflictKey,
        getLegacySyncConflictKey(selectedProjectId)
      ),
    ]);

    setPendingCount(Array.isArray(queue) ? queue.length : 0);
    setSyncConflicts(Array.isArray(conflicts) ? conflicts : []);
  }, [selectedProjectId, user?.id]);

  const updateConflictList = useCallback(
    async (updater) => {
      if (!selectedProjectId) return;

      const conflictKey = getSyncConflictKey(user?.id, selectedProjectId);
      const current = await loadConflictsByKey(
        conflictKey,
        getLegacySyncConflictKey(selectedProjectId)
      );
      const next = updater(Array.isArray(current) ? current : []);
      await saveConflictsByKey(
        conflictKey,
        next,
        getLegacySyncConflictKey(selectedProjectId)
      );
      setSyncConflicts(next);
    },
    [selectedProjectId, user?.id]
  );

  const resolveLocalConflictTask = useCallback(
    (conflict) => {
      const task = conflict?.task;
      if (!task) return null;

      return {
        ...task,
        projectId: task.projectId || selectedProjectId,
        baseUpdatedAt: conflict?.current?.updatedAt ?? task.baseUpdatedAt ?? null,
      };
    },
    [selectedProjectId]
  );

  const handleUseServerConflict = useCallback(
    async (conflict, index) => {
      await updateConflictList((items) =>
        items.filter((item, itemIndex) =>
          conflict?.id ? item.id !== conflict.id : itemIndex !== index
        )
      );
      setSyncPulse((value) => value + 1);
    },
    [updateConflictList]
  );

  const handleUseLocalConflict = useCallback(
    async (conflict, index) => {
      const retryTask = resolveLocalConflictTask(conflict);
      if (!retryTask) return;

      await addToSyncQueue(retryTask, retryTask.projectId || selectedProjectId);
      await updateConflictList((items) =>
        items.filter((item, itemIndex) =>
          conflict?.id ? item.id !== conflict.id : itemIndex !== index
        )
      );
      setSyncPulse((value) => value + 1);
    },
    [addToSyncQueue, resolveLocalConflictTask, selectedProjectId, updateConflictList]
  );

  const handleDismissConflict = useCallback(
    async (conflict, index) => {
      await updateConflictList((items) =>
        items.filter((item, itemIndex) =>
          conflict?.id ? item.id !== conflict.id : itemIndex !== index
        )
      );
    },
    [updateConflictList]
  );

  const handleDismissAllConflicts = useCallback(async () => {
    const confirmed = window.confirm(
      "Tüm çakışmaları listeden kaldırmak istiyor musunuz? Sunucudaki mevcut kayıtlar korunur, yereldeki bekleyen değişiklikler tekrar uygulanmaz."
    );
    if (!confirmed) return;

    await updateConflictList(() => []);
    setSyncPulse((value) => value + 1);
    setIsConflictPanelOpen(false);
  }, [updateConflictList]);

  useEffect(() => {
    refreshSyncState().catch((error) => {
      console.error("Senkronizasyon durumu okunamadı:", error);
    });
  }, [refreshSyncState, syncPulse]);

  useEffect(() => {
    const handleSyncConflict = (event) => {
      refreshSyncState().catch((error) => {
        console.error("Senkronizasyon durumu okunamadı:", error);
      });
      setIsConflictPanelOpen(true);
    };

    window.addEventListener("geo-sync-conflict", handleSyncConflict);
    return () => {
      window.removeEventListener("geo-sync-conflict", handleSyncConflict);
    };
  }, [refreshSyncState]);

  useEffect(() => {
    if (!isOnline || !user || !selectedProjectId) {
      setPersonnel([]);
      return;
    }

    let cancelled = false;

    const loadPersonnel = async () => {
      try {
        const nextPersonnel = await fetchProjectPersonnel(selectedProjectId);
        if (!cancelled) {
          setPersonnel(nextPersonnel);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Personeller alınamadı:", error);
        }
      }
    };

    loadPersonnel();
    return () => {
      cancelled = true;
    };
  }, [isOnline, selectedProjectId, syncPulse, user]);

  const {
    sicaklik,
    setSicaklik,
    atm,
    setAtm,
    spc,
    setSpc,
    tds,
    setTds,
    sal,
    setSal,
    ph,
    setPh,
    notMetni,
    setNotMetni,
    customMeasurements,
    setCustomMeasurements,
    pointPhotos,
    setPointPhotos,
    entryStatus,
    setEntryStatus,
    showVeriPopup,
    setShowVeriPopup,
    handleVeriPopupSave,
    resetFields,
  } = useMeasurements({
    points,
    setPoints,
    isOnline,
    addToSyncQueue,
    clickedInfo,
    measurementTemplate,
  });

  const getAddressFromCoords = useCallback(async (lat, lng, retry = 1) => {
    try {
      const address = await reverseGeocodeLocation(lat, lng);
      return {
        il: address.il,
        ilce: address.ilce,
        koy: address.koy,
      };
    } catch (error) {
      if (retry > 0) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return getAddressFromCoords(lat, lng, retry - 1);
      }
      return { il: "", ilce: "", koy: "" };
    }
  }, []);

  useEffect(() => {
    if (!isOnline || points.length === 0) return;

    const completeAddress = async () => {
      const incompletePoints = points.filter(
        (point) => !point.adresTamamlandi || !point.il || !point.ilce || !point.koy
      );

      if (!incompletePoints.length) return;

      const refreshedPoints = [];

      for (const point of incompletePoints) {
        if (point.lat == null || point.lng == null) continue;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const address = await getAddressFromCoords(point.lat, point.lng);

        if (!address.il && !address.ilce && !address.koy) continue;

        const updates = {
          il: address.il,
          ilce: address.ilce,
          koy: address.koy,
          adresTamamlandi: true,
        };

        try {
          const updatedPoint = await apiUpdatePoint(point.id, {
            ...updates,
            baseUpdatedAt: point.updatedAt ?? null,
          });
          refreshedPoints.push(updatedPoint);
        } catch (error) {
          if (isConflictError(error)) {
            const current = getConflictCurrent(error);
            if (current?.id) {
              refreshedPoints.push(current);
            }
            continue;
          }

          console.warn("Adres bilgisi kaydedilemedi:", error);
          await addToSyncQueue(
            {
              type: "updatePoint",
              projectId: selectedProjectId,
              pointId: point.id,
              baseUpdatedAt: point.updatedAt ?? null,
              updates,
            },
            selectedProjectId
          );
        }
      }

      if (refreshedPoints.length) {
        setPoints((previous) =>
          refreshedPoints.reduce(
            (nextPoints, update) =>
              applyPointServerFields(nextPoints, update.id, update, [
                "il",
                "ilce",
                "koy",
                "adresTamamlandi",
              ]),
            previous
          )
        );
      }
    };

    completeAddress();
  }, [addToSyncQueue, getAddressFromCoords, isOnline, points, selectedProjectId, setPoints]);

  const handleKMZUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !isOnline) {
      alert("Dosya seçilmedi veya internet bağlantısı yok.");
      return;
    }

    try {
      const { points: importedPoints, teams: importedTeams } = await parseKMZ(file);
      if (!importedPoints.length) {
        alert("Dosya içinde nokta bulunamadı.");
        return;
      }

      const paftaPayload = {
        pafta: {
          id: await createFileImportId(selectedProjectId, file),
          isim: file.name,
          gorunur: true,
          aktif: true,
          markersCount: importedPoints.length,
        },
        teams: importedTeams,
        points: importedPoints,
      };

      if (
        paftalar.some(
          (pafta) => pafta.isim.trim().toLowerCase() === file.name.trim().toLowerCase()
        )
      ) {
        alert("Bu pafta zaten mevcut.");
        return;
      }

      let created;
      try {
        created = await importPafta(selectedProjectId, paftaPayload);
      } catch (firstError) {
        const status = firstError?.response?.status;
        if (status && status < 500) throw firstError;
        // İlk istek sunucuda tamamlanıp yalnız yanıt kaybolmuş olabilir; aynı pafta
        // kimliğiyle tek güvenli tekrar yapılır ve sunucu mevcut kaydı döndürür.
        created = await importPafta(selectedProjectId, paftaPayload);
      }
      setPaftalar((previous) => [...previous, created.pafta]);
      setTeams((previous) => [...previous, ...(created.teams || [])]);
      setPoints((previous) => [...previous, ...(created.points || [])]);
      alert(`"${file.name}" başarıyla yüklendi.`);
      } catch (error) {
        console.error("KMZ yükleme hatası:", error);
        alert(getApiErrorMessage(error, "KMZ/KML içe aktarma sırasında hata oluştu."));
      } finally {
        event.target.value = null;
      }
    };

  const startMoveMode = (pointId) => {
    if (paftalar.filter((pafta) => pafta.gorunur).length > 1) {
      alert("Taşıma işlemi için yalnızca tek pafta açık olmalıdır.");
      return;
    }

    setMoveModeId(pointId);
    setTempPosition(null);
    setClickMarker(null);
  };

  const cancelMove = () => {
    setMoveModeId(null);
    setTempPosition(null);
  };

  const confirmMoveToTemp = async () => {
    if (!moveModeId || !tempPosition) {
      alert("Taşıma bilgileri eksik.");
      cancelMove();
      return;
    }

    const point = points.find((item) => item.id === moveModeId);
    if (!point) return;

    // Konum düzeltmesi analiz sonucunu temsil etmez; mevcut durum ve marker rengi korunur.
    const updates = {
      lat: tempPosition.lat,
      lng: tempPosition.lng,
    };
    const baseUpdatedAt = point.updatedAt ?? null;

    setPoints((previous) =>
      previous.map((item) => (item.id === moveModeId ? { ...item, ...updates } : item))
    );

    if (!isOnline) {
      await addToSyncQueue({
        type: "updatePoint",
        pointId: moveModeId,
        baseUpdatedAt,
        updates,
      });
    } else {
      try {
        const updatedPoint = await apiUpdatePoint(moveModeId, {
          ...updates,
          baseUpdatedAt,
        });
        setPoints((previous) =>
          applyPointServerFields(previous, moveModeId, updatedPoint, [
            "lat",
            "lng",
          ])
        );
      } catch (error) {
        if (isConflictError(error)) {
          const current = getConflictCurrent(error);
          let queuedForRetry = false;
          if (current?.id) {
            // Takım atama gibi paralel bir güncelleme araya girdiyse,
            // konum değişikliğini güncel sürüm üzerinde bir kez daha deneriz.
            try {
              const retriedPoint = await apiUpdatePoint(moveModeId, {
                ...updates,
                baseUpdatedAt: current.updatedAt ?? null,
              });
              setPoints((previous) =>
                applyPointServerFields(previous, moveModeId, retriedPoint, [
                  "lat",
                  "lng",
                ])
              );
              cancelMove();
              return;
            } catch (retryError) {
              const latestCurrent = isConflictError(retryError)
                ? getConflictCurrent(retryError)
                : null;

              await addToSyncQueue({
                type: "updatePoint",
                pointId: moveModeId,
                baseUpdatedAt: latestCurrent?.updatedAt ?? current.updatedAt ?? null,
                updates,
              });
              queuedForRetry = true;

              if (latestCurrent?.id) {
                setPoints((previous) =>
                  previous.map((item) =>
                    item.id === latestCurrent.id
                      ? { ...item, ...latestCurrent, ...updates }
                      : item
                  )
                );
              }
            }
          } else {
            await addToSyncQueue({
              type: "updatePoint",
              pointId: moveModeId,
              baseUpdatedAt,
              updates,
            });
            queuedForRetry = true;
          }
          alert(
            getApiErrorMessage(
              error,
              queuedForRetry
                ? "Nokta aynı anda güncellendi. Konum değişikliği kuyruğa alındı."
                : "Nokta başka bir oturumda güncellendi."
            )
          );
          cancelMove();
          return;
        }

        await addToSyncQueue({
          type: "updatePoint",
          pointId: moveModeId,
          baseUpdatedAt,
          updates,
        });
      }
    }

    cancelMove();
  };

  const downloadPafta = async (pafta) => {
    const paftaPoints = points.filter((point) => point.paftaId === pafta.id);
    if (!paftaPoints.length) {
      alert("Bu paftada indirilecek nokta yok.");
      return;
    }

    const zip = new JSZip();
    const styleMap = {
      Pozitif: "ff0000ff",
      Negatif: "ff00ff00",
      Tekrar: "ff00ffff",
      "Konum Güncellendi": "ffff00ff",
      Yeni: "ff00ffff",
      pending: "ff000000",
      Bilinmiyor: "ffffffff",
    };

    const styles = Object.entries(styleMap)
      .map(
        ([key, value]) =>
          `<Style id="${key}"><IconStyle><color>${value}</color><scale>1.1</scale><Icon><href>http://maps.google.com/mapfiles/kml/pushpin/red-pushpin.png</href></Icon></IconStyle></Style>`
      )
      .join("\n");

    const placemarks = paftaPoints
      .map(
        (point) =>
          `<Placemark><name>${point.name || point.id}</name><styleUrl>#${point.status || "Bilinmiyor"}</styleUrl><Point><coordinates>${point.lng},${point.lat},0</coordinates></Point></Placemark>`
      )
      .join("\n");

    zip.file(
      "doc.kml",
      `<?xml version="1.0" encoding="UTF-8"?><kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${pafta.isim}</name>${styles}${placemarks}</Document></kml>`
    );

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `${pafta.isim.replace(/\.[^/.]+$/, "")}_guncel.kmz`);
  };

  const fillAndDownloadNATTForm = async () => {
    const visiblePaftas = paftalar.filter((pafta) => pafta.gorunur);
    if (visiblePaftas.length !== 1) {
      alert("Dışa aktarmak için yalnızca bir pafta seçin.");
      return;
    }

    const activePafta = visiblePaftas[0];
    let filteredPoints = points.filter((point) => point.paftaId === activePafta.id);

    if (nattFilterStart || nattFilterEnd) {
      const start = nattFilterStart ? new Date(nattFilterStart).getTime() : 0;
      const end = nattFilterEnd ? new Date(nattFilterEnd).getTime() + 86400000 : Infinity;
      filteredPoints = filteredPoints.filter((point) => {
        const timestamp = point.statusUpdatedAt ?? point.veriGirisiTimestamp;
        return timestamp >= start && timestamp <= end;
      });
    }

    const normalizeSampleText = (value) =>
      String(value ?? "")
        .trim()
        .toLocaleLowerCase("tr-TR");

    const getExportSampleType = (point) => {
      const custom = point?.customMeasurements || {};
      const sampleType = String(
        custom.numuneTuru ??
          custom["Numune Türü"] ??
          point?.numuneTuru ??
          point?.numuneTuruAdi ??
          ""
      ).trim();

      if (!sampleType) return "";
      if (normalizeSampleText(sampleType) !== "su") return sampleType;

      const waterType = String(
        custom.suNumuneTuru ??
          custom["Su Türü"] ??
          custom["Su Numune Türü"] ??
          custom.suTuru ??
          custom.suTipi ??
          point?.suNumuneTuru ??
          point?.suTuru ??
          ""
      ).trim();

      return waterType || sampleType;
    };

    const visibleTemplateRows = [...(measurementTemplate || [])]
      .filter((row) => row?.visible !== false)
      .sort((a, b) => (a?.order ?? 999) - (b?.order ?? 999));

    const getTemplateCellValue = (point, row) => {
      const custom = point?.customMeasurements || {};
      if (row?.bind) {
        const boundValue = point?.[row.bind];
        return boundValue == null ? "" : boundValue;
      }

      const rowId = row?.id;
      if (rowId && custom[rowId] != null) return custom[rowId];

      const rowLabel = String(row?.label || "").trim();
      if (rowLabel && custom[rowLabel] != null) return custom[rowLabel];

      return "";
    };

    const legacyRows = filteredPoints.map((point, index) => ({
      Sıra: point.name || index + 1,
      Pafta: point.pafta,
      Ad: point.name,
      İl: point.il,
      İlçe: point.ilce,
      Köy: point.koy,
      Enlem: point.lat,
      Boylam: point.lng,
      Durum: point.status,
      "Numune Türü": getExportSampleType(point),
      ...Object.fromEntries(
        Object.entries(point.customMeasurements || {}).filter(
          ([key]) =>
            ![
              "numuneTuru",
              "Numune Türü",
              "suNumuneTuru",
              "Su Türü",
              "Su Numune Türü",
              "suTuru",
              "suTipi",
            ].includes(String(key))
        )
      ),
    }));

    void legacyRows;

    const rows = filteredPoints.map((point, index) => {
      const row = {
        "S\u0131ra": point.name || index + 1,
        "Pafta": point.pafta,
        "Ad": point.name,
        "\u0130l": point.il,
        "\u0130l\u00E7e": point.ilce,
        "K\u00F6y": point.koy,
        "Enlem": point.lat,
        "Boylam": point.lng,
        "Durum": point.status,
        "Numune T\u00FCr\u00FC": getExportSampleType(point),
      };

      visibleTemplateRows.forEach((templateRow) => {
        const header = String(templateRow?.label || "").trim();
        if (!header) return;
        row[header] = getTemplateCellValue(point, templateRow);
      });

      return row;
    });

    const { default: ExcelJS } = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("NATT");
    const columnKeys = [...new Set(rows.flatMap((row) => Object.keys(row)))];

    worksheet.columns = columnKeys.map((key) => ({
      header: key,
      key,
      width: Math.min(Math.max(String(key || "").length + 4, 14), 28),
    }));

    rows.forEach((row) => {
      worksheet.addRow(row);
    });

    worksheet.getRow(1).font = { bold: true };
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `NATT_${activePafta.isim}.xlsx`
    );
  };

  const visiblePoints = useMemo(() => {
    return points.filter((point) => {
      const pafta = paftalar.find((item) => item.id === point.paftaId);
      if (!pafta || !pafta.gorunur) return false;
      if (!selectedTeamId || selectedTeamId === "Hepsi") return true;
      if (selectedTeamId === "Atanmamis") return !point.ekipId;
      return point.ekipId === selectedTeamId;
    });
  }, [paftalar, points, selectedTeamId]);

  const aktifPaftaSayisi = paftalar.filter((pafta) => pafta.aktif).length;
  const pasifPaftaSayisi = paftalar.filter((pafta) => !pafta.aktif).length;

  const isAuthorized = useMemo(() => {
    if (!user) return false;
    if (user.role === "superadmin" || user.projectRoles?.ALL === "superadmin") {
      return true;
    }
    if (!projects.length) {
      return true;
    }
    return authorizedProjectIds.some(
      (projectId) => String(projectId) === String(selectedProjectId)
    );
  }, [authorizedProjectIds, projects.length, selectedProjectId, user]);

  const canManagePaftaAndTeams = useMemo(() => {
    if (!user || !selectedProjectId) return false;
    if (user.role === "superadmin" || user.projectRoles?.ALL === "superadmin") {
      return true;
    }
    if (user.projectRoles?.[selectedProjectId] === "project_admin") {
      return true;
    }
    return (
      user.role === "project_admin" &&
      String(user.projectId || "") === String(selectedProjectId)
    );
  }, [selectedProjectId, user]);

  const activeToolPageDefinition = useMemo(
    () =>
      [...TOOL_PAGES, PACKAGING_PAGE].find((item) => item.id === activeToolPage) ||
      null,
    [activeToolPage]
  );
  const isMapViewActive = !activeToolPageDefinition;

  useEffect(() => {
    if (isMapViewActive || !isCoordinateSearchOpen) return;
    setCoordinateSearchError("");
    setIsCoordinateSearchOpen(false);
  }, [isCoordinateSearchOpen, isMapViewActive]);

  const handleCloseToolPage = useCallback(() => {
    setActiveToolPage(null);
    setIsToolsMenuOpen(false);
  }, []);

  const handleSelectToolPage = useCallback((pageId) => {
    // Araç sayfaları harita akışından ayrıdır; geçişte açık harita popup'larını kapatalım.
    setShowVeriPopup(false);
    setIsTeamModalOpen(false);
    setIsConflictPanelOpen(false);
    setSelectedPaftaForTeams(null);
    setClickedInfo(null);
    setClickMarker(null);
    setTempPosition(null);
    setMoveModeId(null);
    setIsAdminViewOpen(false);
    setIsToolsMenuOpen(false);
    setCoordinateSearchError("");
    setIsCoordinateSearchOpen(false);
    setCoordinateSearchX("");
    setCoordinateSearchY("");
    setCoordinateSearchTarget(null);
    setActiveToolPage(pageId);
  }, [setShowVeriPopup]);

  const handleCoordinateSearch = useCallback((event) => {
    event.preventDefault();

    const result = parseCoordinateSearch(coordinateSearchX, coordinateSearchY);
    if (result.error) {
      setCoordinateSearchError(result.error);
      return;
    }

    const { lat, lng } = result.coordinate;
    setCoordinateSearchError("");
    setCoordinateSearchX("");
    setCoordinateSearchY("");
    setShowVeriPopup(false);
    setIsTeamModalOpen(false);
    setIsConflictPanelOpen(false);
    setSelectedPaftaForTeams(null);
    setClickedInfo(null);
    setClickMarker(null);
    setTempPosition(null);
    setMoveModeId(null);
    setIsAdminViewOpen(false);
    setIsToolsMenuOpen(false);
    setActiveToolPage(null);
    setIsCoordinateSearchOpen(false);
    setCoordinateSearchTarget({ lat, lng, id: Date.now() });
  }, [coordinateSearchX, coordinateSearchY, setShowVeriPopup]);

  const handleOpenCoordinateSearch = useCallback(() => {
    setCoordinateSearchX("");
    setCoordinateSearchY("");
    setCoordinateSearchError("");
    setIsCoordinateSearchOpen(true);
  }, []);

  const handleCloseCoordinateSearch = useCallback(() => {
    setCoordinateSearchError("");
    setIsCoordinateSearchOpen(false);
  }, []);

  const handleClearCoordinateSearchForm = useCallback(() => {
    setCoordinateSearchX("");
    setCoordinateSearchY("");
    setCoordinateSearchError("");
  }, []);

  const handleClearCoordinateSearch = useCallback(() => {
    setCoordinateSearchX("");
    setCoordinateSearchY("");
    setCoordinateSearchError("");
    setCoordinateSearchTarget(null);
  }, []);

  const selectedProjectDisplayName = useMemo(() => {
    if (!selectedProjectId || selectedProjectId === LEGACY_DEFAULT_PROJECT_ID) {
      return "Proje seçimi bekleniyor";
    }

    return (
      projects.find((project) => String(project.id) === String(selectedProjectId))?.name ||
      "Proje yükleniyor"
    );
  }, [projects, selectedProjectId]);

  const globalRoleLabel = useMemo(
    () => getGlobalRoleLabel(user, selectedProjectId),
    [selectedProjectId, user]
  );

  const topBarUserName =
    user?.displayName || user?.name || user?.username || "Kullanıcı";
  const isSuperAdmin =
    user?.role === "superadmin" || user?.projectRoles?.ALL === "superadmin";

  if (!authReady) {
    return (
      <div className="app" style={{ display: "grid", placeItems: "center", height: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <div className="spinner" style={{ marginBottom: 16 }} />
          <div>Oturum hazırlanıyor...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if ((user.role === "superadmin" || user.role === "project_admin") && isAdminViewOpen) {
    return (
      <AdminPanel
        user={user}
        selectedProjectId={selectedProjectId}
        onLogout={handleLogout}
        onBackToMap={() => setIsAdminViewOpen(false)}
      />
    );
  }

  return (
    <div className="app">
      {!isAuthorized ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 11000,
            background: "rgba(15, 23, 42, 0.95)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
          }}
        >
          <div
            style={{
              textAlign: "center",
              padding: 40,
              borderRadius: 16,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <h2 style={{ fontSize: "1.8rem", fontWeight: 800, marginBottom: 10 }}>
              Bu projede yetkiniz yok
            </h2>
            <p style={{ opacity: 0.7, maxWidth: 400, margin: "0 auto 30px" }}>
              Lütfen size atanmış bir projeyi seçin veya farklı bir hesapla giriş yapın.
            </p>
            <button
              onClick={handleLogout}
              style={{
                padding: "12px 24px",
                background: "#ef4444",
                border: "none",
                borderRadius: 8,
                color: "white",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Farklı Hesapla Giriş Yap
            </button>
          </div>
        </div>
      ) : null}

      {isSwitching ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeInOut 0.8s ease-in-out",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div className="spinner" style={{ marginBottom: 15, width: 60, height: 60, borderWidth: 6 }} />
            <h2 style={{ color: "#1e293b", fontWeight: 800, fontSize: "1.5rem" }}>
              Bağlanıyor...
            </h2>
            <p style={{ opacity: 0.6 }}>Lütfen bekleyin, çalışma alanı hazırlanıyor.</p>
          </div>
        </div>
      ) : null}

      {!isOnline ? (
        <div
          style={{
            position: "fixed",
            top: 68,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            background: "#ef4444",
            color: "white",
            padding: "4px 12px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: "bold",
            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            pointerEvents: "none",
          }}
        >
          Çevrimdışı mod: Değişiklikler internet geldiğinde senkronize edilir.
        </div>
      ) : null}

      {projectAccessNotice ? (
        <div
          style={{
            position: "fixed",
            top: isOnline ? 68 : 100,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9998,
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            color: "#ffffff",
            padding: "8px 14px",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            boxShadow: "0 8px 20px rgba(120, 53, 15, 0.3)",
            pointerEvents: "none",
            maxWidth: "90vw",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {projectAccessNotice}
        </div>
      ) : null}

      {(pendingCount > 0 || syncConflicts.length > 0) ? (
        <button
          type="button"
          onClick={() => setIsConflictPanelOpen(true)}
          style={{
            position: "fixed",
            right: 20,
            bottom: 20,
            zIndex: 9000,
            border: "none",
            borderRadius: 16,
            padding: "12px 16px",
            background:
              syncConflicts.length > 0
                ? "linear-gradient(135deg, #b91c1c, #ef4444)"
                : "linear-gradient(135deg, #1d4ed8, #3b82f6)",
            color: "#fff",
            fontWeight: 800,
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.26)",
            cursor: "pointer",
            minWidth: 190,
            textAlign: "left",
          }}
        >
          <div style={{ fontSize: "0.95rem", marginBottom: 2 }}>Senkron Durumu</div>
          <div style={{ fontSize: "0.8rem", opacity: 0.92 }}>
            Bekleyen: {pendingCount} | Çakışma: {syncConflicts.length}
          </div>
        </button>
      ) : null}

      <button className="toggle-menu" style={{ display: "none" }} onClick={() => setMenuVisible((value) => !value)}>
        {menuVisible ? "Kapat" : "Menü"}
      </button>

      <div className={`global-topbar ${menuVisible ? "" : "global-topbar--collapsed"}`.trim()}>
        <div className="global-topbar__section global-topbar__section--left">
          <button
            type="button"
            className="global-topbar__button global-topbar__button--ghost"
            onClick={handleToggleSidebarMenu}
          >
            {menuVisible ? "Menüyü Kapat" : "Menü"}
          </button>
          <div
            className="global-topbar__project-context"
            title={`Aktif proje: ${selectedProjectDisplayName}`}
            aria-label={`Aktif proje: ${selectedProjectDisplayName}`}
          >
            <span className="global-topbar__project-context-marker" aria-hidden="true" />
            <span className="global-topbar__project-context-name">
              {selectedProjectDisplayName}
            </span>
          </div>
        </div>

        {isMapViewActive ? (
          <div className="global-topbar__coordinate">
          <button
            type="button"
            className="global-topbar__coordinate-button"
            onClick={handleOpenCoordinateSearch}
          >
            Koordinat Ara
          </button>
          {coordinateSearchTarget ? (
            <button
              type="button"
              className="global-topbar__coordinate-clear"
              onClick={handleClearCoordinateSearch}
              aria-label="Koordinat aramasını temizle"
            >
              ×
            </button>
          ) : null}
          </div>
        ) : null}

        <div className="global-topbar__actions">
          <div
            ref={toolsMenuRef}
            className="global-topbar__tools"
            aria-label="Araç araçları"
          >
            <button
              type="button"
              className={`global-topbar__tools-trigger ${
                isToolsMenuOpen ? "global-topbar__tools-trigger--open" : ""
              }`.trim()}
              onClick={() => setIsToolsMenuOpen((value) => !value)}
              aria-expanded={isToolsMenuOpen}
              aria-haspopup="menu"
            >
              <span className="global-topbar__tools-trigger-label">Araç</span>
              <span className="global-topbar__tools-trigger-caret">
                {isToolsMenuOpen ? "▴" : "▾"}
              </span>
            </button>

            {isToolsMenuOpen ? (
              <div className="global-topbar__tools-menu" role="menu">
                {TOOL_PAGES.map((toolPage) => (
                  <button
                    key={toolPage.id}
                    type="button"
                    role="menuitem"
                    className={`global-topbar__tools-menu-item ${
                      activeToolPage === toolPage.id
                        ? "global-topbar__tools-menu-item--active"
                        : ""
                    }`.trim()}
                    onClick={() => handleSelectToolPage(toolPage.id)}
                  >
                    {toolPage.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className={`global-topbar__button global-topbar__button--packaging ${
              activeToolPage === PACKAGING_PAGE.id ? "global-topbar__button--active" : ""
            }`.trim()}
            onClick={() => handleSelectToolPage(PACKAGING_PAGE.id)}
          >
            Paketleme
          </button>

          <div className="global-topbar__identity" aria-label="Oturum bilgileri">
            {!isSuperAdmin ? (
              <span className="global-topbar__user">{topBarUserName}</span>
            ) : null}
            <span className="global-topbar__pill global-topbar__pill--accent">
              {globalRoleLabel}
            </span>
          </div>

          {(user.role === "superadmin" || user.role === "project_admin") ? (
            <button
              type="button"
              className="global-topbar__button global-topbar__button--primary"
              onClick={handleOpenAdminView}
              disabled={isOpeningAdminView}
            >
              {isOpeningAdminView ? "Kontrol Ediliyor..." : "Admin Paneli"}
            </button>
          ) : null}

          <button
            type="button"
            className="global-topbar__button global-topbar__button--danger"
            onClick={handleLogout}
          >
            Çıkış
          </button>
        </div>
      </div>

      {isCoordinateSearchOpen ? (
        <CoordinateSearchModal
          coordinateSearchError={coordinateSearchError}
          coordinateSearchX={coordinateSearchX}
          coordinateSearchY={coordinateSearchY}
          onClear={handleClearCoordinateSearchForm}
          onClose={handleCloseCoordinateSearch}
          onSubmit={handleCoordinateSearch}
          setCoordinateSearchError={setCoordinateSearchError}
          setCoordinateSearchX={setCoordinateSearchX}
          setCoordinateSearchY={setCoordinateSearchY}
        />
      ) : null}

      {activeToolPageDefinition ? (
        <ToolPageHost
          activeToolPageDefinition={activeToolPageDefinition}
          canDeleteVehicleRecords={canManagePaftaAndTeams}
          currentUserId={user.id}
          currentUserName={topBarUserName}
          isOnline={isOnline}
          onBack={handleCloseToolPage}
          projects={projects}
          selectedProjectId={selectedProjectId}
        />
      ) : (
        <>
          <SidebarPanel
            menuVisible={menuVisible}
            user={user}
            projects={projects}
            selectedProjectId={selectedProjectId}
            onProjectChange={handleProjectChange}
            createEmptyProject={createEmptyProject}
            renameProject={renameProject}
            deleteProject={deleteProject}
            paftaTab={paftaTab}
            setPaftaTab={setPaftaTab}
            aktifPaftaSayisi={aktifPaftaSayisi}
            pasifPaftaSayisi={pasifPaftaSayisi}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedTeamId={selectedTeamId}
            setSelectedTeamId={setSelectedTeamId}
            teams={teams}
            paftalar={paftalar}
            points={points}
            fillAndDownloadNATTForm={fillAndDownloadNATTForm}
            paftaGorunurlukDegistir={paftaGorunurlukDegistir}
            makePaftaPasif={makePaftaPasif}
            makePaftaAktif={makePaftaAktif}
            setSelectedPaftaForTeams={setSelectedPaftaForTeams}
            deletePafta={deletePafta}
            downloadPafta={downloadPafta}
            handleKMZUpload={handleKMZUpload}
            nattFilterStart={nattFilterStart}
            setNattFilterStart={setNattFilterStart}
            nattFilterEnd={nattFilterEnd}
            setNattFilterEnd={setNattFilterEnd}
            openTeamModal={() => setIsTeamModalOpen(true)}
            canManagePaftaAndTeams={canManagePaftaAndTeams}
          />

          <MapSection
            key={selectedProjectId || "no-project"}
            gpsPosition={gpsPosition}
            clickMarker={clickMarker}
            setClickMarker={setClickMarker}
            moveModeId={moveModeId}
            tempPosition={tempPosition}
            setTempPosition={setTempPosition}
            confirmMoveToTemp={confirmMoveToTemp}
            cancelMove={cancelMove}
            visiblePoints={visiblePoints}
            points={points}
            selectedProjectId={selectedProjectId}
            paftalar={paftalar}
            teams={teams}
            changePointStatus={changePointStatus}
            setClickedInfo={setClickedInfo}
            setSicaklik={setSicaklik}
            setAtm={setAtm}
            setSpc={setSpc}
            setTds={setTds}
            setSal={setSal}
            setPh={setPh}
            setNotMetni={setNotMetni}
            setShowVeriPopup={setShowVeriPopup}
            assignTeamToPoint={assignTeamToPoint}
            startMoveMode={startMoveMode}
            selectedPaftaForTeams={selectedPaftaForTeams}
            setSelectedPaftaForTeams={setSelectedPaftaForTeams}
            deleteTeam={deleteTeam}
            handleDeleteMember={handleDeleteMember}
            selectedTeamId={selectedTeamId}
            showVeriPopup={showVeriPopup}
            createPointLocalFirst={createPointLocalFirst}
            canManageTeams={canManagePaftaAndTeams}
            coordinateSearchTarget={coordinateSearchTarget}
          />

          <DataEntryPopup
            showVeriPopup={showVeriPopup}
            clickedInfo={clickedInfo}
            sicaklik={sicaklik}
            atm={atm}
            spc={spc}
            tds={tds}
            sal={sal}
            ph={ph}
            notMetni={notMetni}
            customMeasurements={customMeasurements}
            pointPhotos={pointPhotos}
            entryStatus={entryStatus}
            setCustomMeasurements={setCustomMeasurements}
            setPointPhotos={setPointPhotos}
            setEntryStatus={setEntryStatus}
            measurementTemplate={measurementTemplate}
            addTemplateRow={addTemplateRow}
            renameTemplateRow={renameTemplateRow}
            deleteTemplateRow={deleteTemplateRow}
            moveTemplateRow={moveTemplateRow}
            resetTemplateToDefault={resetTemplateToDefault}
            setSicaklik={setSicaklik}
            setAtm={setAtm}
            setSpc={setSpc}
            setTds={setTds}
            setSal={setSal}
            setPh={setPh}
            setNotMetni={setNotMetni}
            setShowVeriPopup={setShowVeriPopup}
            setPoints={setPoints}
            onCancel={resetFields}
            onSave={handleVeriPopupSave}
          />

          <TeamPersonnelModal
            isOpen={isTeamModalOpen}
            onClose={() => setIsTeamModalOpen(false)}
            teams={teams}
            personnel={personnel}
            paftalar={paftalar}
            addNewTeam={addNewTeam}
            deleteTeam={deleteTeam}
            handleAddMember={handleAddMember}
            handleRemoveMemberByName={(teamId, memberName) => {
              const team = teams.find((item) => item.id === teamId);
              if (!team?.members) return;
              const index = team.members.indexOf(memberName);
              if (index > -1) {
                handleDeleteMember(teamId, index);
              }
            }}
            canManageTeams={canManagePaftaAndTeams}
          />

          <SyncConflictPanel
            open={isConflictPanelOpen}
            onClose={() => setIsConflictPanelOpen(false)}
            conflicts={syncConflicts}
            pendingCount={pendingCount}
            isOnline={isOnline}
            onUseServer={handleUseServerConflict}
            onUseLocal={handleUseLocalConflict}
            onDismiss={handleDismissConflict}
            onDismissAll={handleDismissAllConflicts}
          />
        </>
      )}
    </div>
  );
}
